import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GET_SERVER } from 'app/server/Api';
import useAuths from 'app/hooks/auth/useAuth';

const POLL_INTERVAL_MS = 30000;
const SEEN_IDS_KEY = 'bannerRequestNotifier_seenIds';
const MAX_SEEN_IDS = 100;

const loadSeenIds = () => {
  try {
    const raw = localStorage.getItem(SEEN_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveSeenIds = (ids) => {
  try {
    localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(ids.slice(-MAX_SEEN_IDS)));
  } catch (e) {
    // localStorage unavailable (private mode, quota, etc.) — degrade
    // silently, dedupe just won't survive a reload.
  }
};

const getSizeLabelText = (row) => {
  if (row?.size_label === 'custom') {
    return `Custom ${row.custom_width_cm || '?'}x${row.custom_height_cm || '?'}cm`;
  }
  return `${row?.size_label || '1x1'} Meter`;
};

/**
 * Mounted once at the app root (see App.jsx) — NOT inside the Request
 * Banner page itself — so it keeps polling for new PENDING banner
 * requests no matter which menu the admin currently has open, and pops a
 * toast in the bottom-right corner the moment one arrives. The whole
 * point is covering an admin who forgets to check the "Request Banner"
 * menu on their own.
 */
export default function BannerRequestNotifier() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuths();
  const seenIdsRef = useRef(loadSeenIds());
  const isFirstPollRef = useRef(true);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        await GET_SERVER(
          '/banner-requests?status=PENDING&limit=10&page=1',
          null,
          (res) => {
            if (cancelled) return;
            const payload = res?.data?.data || res?.data;
            const items = payload?.data || (Array.isArray(payload) ? payload : []);
            if (!Array.isArray(items) || items.length === 0) return;

            // Oldest-first so, if several arrived at once, the toasts pop
            // in the order the requests actually came in.
            const sorted = [...items].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

            const seen = seenIdsRef.current;
            const freshOnes = sorted.filter((row) => !seen.includes(row.id));
            if (freshOnes.length === 0) return;

            // On the very first poll after a login/reload, a fresh browser
            // has no seen-list yet, so every currently-pending request
            // looks "new" — that's actually the point (an admin who hasn't
            // checked in a while should be told what's waiting), but cap
            // it to the 3 most recent so a big backlog doesn't dump a wall
            // of toasts on login.
            const toShow = isFirstPollRef.current ? freshOnes.slice(-3) : freshOnes;
            isFirstPollRef.current = false;

            toShow.forEach((row) => {
              toast.info(
                <div onClick={() => navigate('/website/request-banner')} style={{ cursor: 'pointer' }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>🔔 Request Banner Baru Masuk!</div>
                  <div style={{ fontSize: 13 }}>
                    {row.agent_name || 'Agen'} — {getSizeLabelText(row)}, {row.quantity || 1}x cetak
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{row.request_code}</div>
                </div>,
                {
                  position: 'bottom-right',
                  autoClose: 10000,
                  closeOnClick: true,
                  pauseOnHover: true,
                  theme: 'colored'
                }
              );
            });

            seenIdsRef.current = [...seen, ...freshOnes.map((row) => row.id)].slice(-MAX_SEEN_IDS);
            saveSeenIds(seenIdsRef.current);
          },
          () => {
            // silent — best-effort background reminder, not a critical path
          }
        );
      } catch (e) {
        // silent, same reasoning
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, navigate]);

  return null;
}
